import type { TourScene } from './tour-types';

export const IPD_SCENES: readonly TourScene[] = [
  {
    id: 'ipd-beds',
    chapter: 'i-admit',
    route: '/ipd/beds',
    title: 'Bed board',
    label: 'Bed board',
    caption: {
      heading: 'Every bed in the hospital at a glance',
      body: 'The bed board counts total, occupied, vacant and awaiting-cleaning beds, then lists each ward with its beds and their state. Filter to one ward when you only care about one floor.',
      tip: 'The occupancy figure on the dashboard is worked out from these same beds.',
    },
  },
  {
    id: 'ipd-admit',
    chapter: 'i-admit',
    route: '/ipd/admissions',
    title: 'Admit a patient',
    label: 'Admit',
    caption: {
      heading: 'Admit in one step',
      body: 'Choose the patient, the bed class and a free bed, the admitting consultant and a provisional diagnosis, and take the advance deposit. Admitting occupies the bed, records the deposit and opens the running bill together.',
      tip: 'Only free beds of the chosen class are offered, so two patients can never be given the same bed.',
    },
  },
  {
    id: 'ipd-admission',
    chapter: 'i-admit',
    route: '/ipd/admissions/01a0c481…',
    title: 'The admission',
    label: 'Admission',
    caption: {
      heading: 'One page for the whole stay',
      body: 'The admission opens with its status, the consultant and the account: what has been billed, the advances and payments taken, and the credit left. New order, Transfer bed and Discharge are buttons on this page, and eMAR, Notes, Vitals and the Running bill are its tabs.',
      tip: 'The first bed charge is billed at admission, and the deposit sits against it as credit.',
    },
  },
  {
    id: 'ipd-order',
    chapter: 'i-care',
    route: '/ipd/admissions/01a0c481…',
    title: 'Physician order',
    label: 'Orders',
    caption: {
      heading: 'The doctor writes the orders for the ward',
      body: 'A physician order can be a medication, an IV fluid, a diet, a nursing instruction, a lab or radiology request or a procedure. For a medicine give the dose, route, frequency and times, or tick STAT to give it once, now.',
      tip: 'The dose schedule is generated from the times you give, so the nurse never has to re-type the order. A high-alert medicine can require a second nurse to witness each dose.',
    },
  },
  {
    id: 'ipd-progress-note',
    chapter: 'i-care',
    route: '/ipd/admissions/01a0c481…',
    title: 'Progress note',
    label: 'Progress note',
    caption: {
      heading: 'Doctor’s progress notes in SOAP form',
      body: 'The Notes tab keeps the doctor’s progress notes and the nursing notes in separate cards. A progress note has four boxes: subjective, objective, assessment and plan.',
      tip: 'Each note is kept with the admission, so whoever takes over the ward reads the story in order.',
    },
  },
  {
    id: 'ipd-emar',
    chapter: 'i-care',
    route: '/ipd/admissions/01a0c481…',
    title: 'eMAR',
    label: 'eMAR',
    caption: {
      heading: 'The nurse gives the dose and records it',
      body: 'The eMAR lists every dose that is due, with its time, medicine and status. The nurse presses Record, chooses the outcome, given by default, adds the site, and the Given at column fills in.',
      tip: 'The doses come from the order the doctor wrote, so nothing is copied across by hand.',
    },
  },
  {
    id: 'ipd-vitals',
    chapter: 'i-care',
    route: '/ipd/admissions/01a0c481…',
    title: 'Vitals',
    label: 'Vitals',
    caption: {
      heading: 'Vitals on the ward chart',
      body: 'Record blood pressure, pulse, respiratory rate, temperature, oxygen saturation, height, weight, pain score and blood sugar. Every reading is listed on the Vitals tab with the time it was taken.',
      tip: 'Only fill in what you measured. The list shows the readings that were recorded.',
    },
  },
  {
    id: 'ipd-nursing-note',
    chapter: 'i-care',
    route: '/ipd/admissions/01a0c481…',
    title: 'Nursing note',
    label: 'Nursing note',
    caption: {
      heading: 'Nursing notes and handovers',
      body: 'Each shift the nurse writes a free-text note, or an SBAR handover, for the next shift. It sits on the Notes tab beside the doctor’s progress notes.',
      tip: 'A handover written on the patient’s own chart is not lost between shifts.',
    },
  },
  {
    id: 'ipd-bill',
    chapter: 'i-discharge',
    route: '/ipd/admissions/01a0c481…',
    title: 'Running bill',
    label: 'Running bill',
    caption: {
      heading: 'The bill grows with the stay',
      body: 'The Running bill tab lists every charge so far, grouped by where it came from, starting with the bed charge. Print an interim bill at any time, or the final bill at discharge.',
      tip: 'Credit on the admission is the deposit less what has been billed, so the family always knows where they stand.',
    },
  },
  {
    id: 'ipd-transfer',
    chapter: 'i-discharge',
    route: '/ipd/admissions/01a0c481…',
    title: 'Transfer bed',
    label: 'Transfer',
    caption: {
      heading: 'Move the patient to another bed',
      body: 'Choose the new bed and confirm. The patient moves, the old bed is released for cleaning, and the tariff changes from the moment of transfer.',
      tip: 'The bed charge follows the class of the bed the patient is in, so a move to a private room is billed as one.',
    },
  },
  {
    id: 'ipd-discharge',
    chapter: 'i-discharge',
    route: '/ipd/admissions/01a0c481…',
    title: 'Discharge',
    label: 'Discharge',
    caption: {
      heading: 'Discharge with a summary',
      body: 'Choose the discharge type, then write the final diagnosis, the hospital course, the condition at discharge and the medication to go home on. The summary is signed by the doctor and printed for the patient.',
      tip: 'The dialog warns you about work still open on the admission, and about an account that is short, before you finish.',
    },
  },
  {
    id: 'ipd-after',
    chapter: 'i-discharge',
    route: '/ipd/beds',
    title: 'Bed board after discharge',
    label: 'Beds released',
    caption: {
      heading: 'The beds go back to the ward',
      body: 'Once the patient has gone, the beds they used show as awaiting cleaning, and occupancy drops back. The housekeeping team can turn them over for the next admission.',
      tip: 'The bed is released by the discharge itself, so the board and the dashboard stay accurate.',
    },
  },
];
